// Libraries
import { Box, Grid, GridItem } from '@chakra-ui/react';
import axios from 'axios';
import { signIn } from 'next-auth/react';
import { useState } from 'react';
import { toFormikValidationSchema } from 'zod-formik-adapter';
// Components
import { FormError, FormSubmit, ValidationText } from '@atoms/index';
import { useForm } from '@hooks/index';
import { FormInput } from '@molecules/index';
import { ChangePasswordStatus } from '@organisms/index';
// Data
import { ChangePasswordSchema, ChangePasswordValues } from '@schemas/forms';
import { config } from '@utils/env';
// Utils
import { AppPath, AUTH_PROVIDER, LinersApiPaths } from '@constants/index';
import { getErrorMessage } from '@helpers/index';
import text from '@translations/en.json';
// Types
import { ChangePasswordFormProps } from './ChangePasswordForm.type';

/**
 * Mapping representing validations used for the password.
 */
const validationsMap = [
  { text: text.change_password_minimum, validation: /.{8,}/ },
  { text: text.change_password_lower, validation: /[a-z]/ },
  { text: text.change_password_upper, validation: /[A-Z]/ },
  { text: text.change_password_number, validation: /\d/ },
  { text: text.change_password_special, validation: /[!@#$%^&*()\-_=+{};:,<.>]/ },
];

/**
 * ChangePasswordForm component represents a form for changing the user password.
 * @component
 * @param {ChangePasswordFormProps} props - The component props.
 * @param {string} props.username - The username of the user that is changing the password.
 * @param {string} props.confirmationCode - The confirmation code obtained from aws.
 * @param {ChangePasswordStatus} props.status - The status value for the change password process.
 * @returns {ReactElement} The ChangePasswordForm component.
 */
export const ChangePasswordForm = ({ username, confirmationCode, status }: ChangePasswordFormProps) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isValid = !!!errorMessage;
  const initialValues = {
    username,
    password: '',
    confirmPassword: '',
  };

  /**
   * Handles form submission.
   * @param {LoginValues} values - The form values to be submitted.
   */
  const submitHandler = async (values: ChangePasswordValues) => {
    setIsLoading(true);
    try {
      if (status === ChangePasswordStatus.INITIAL_LOGIN) {
        await axios.post(`${config?.services?.apiGateway}${LinersApiPaths.ConfirmSignUp}`, {
          username,
          password: values.password,
          confirmationCode,
        });
      } else {
        await axios.post(`${config?.services?.apiGateway}${LinersApiPaths.ConfirmForgotPassword}`, {
          username,
          password: values.password,
          confirmationCode,
        });
      }
      const { error } =
        (await signIn(AUTH_PROVIDER, {
          redirect: true,
          callbackUrl: AppPath.HOME,
          username,
          password,
        })) ?? {};
      if (error) throw new Error(error);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setErrorMessage(errorMessage);
      setIsLoading(false);
    }
  };

  const formikCtx = useForm<ChangePasswordValues>({
    initialValues,
    schema: toFormikValidationSchema(ChangePasswordSchema),
    submitHandler,
  });
  const { submitCount } = formikCtx;
  const wasSubmitted = submitCount > 0;
  const password = formikCtx.values.password;

  return (
    <Box as='form' w='full' onSubmit={formikCtx.handleSubmit}>
      <Grid
        gap={{ base: 5, md: 8 }}
        gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
        templateAreas={{ base: `"texts""form""submit"`, md: `"form texts""submit x"` }}
      >
        <GridItem area='form' minW={60} minH={48}>
          <FormInput
            isHidden
            label={text.username}
            wasSubmitted={wasSubmitted}
            id='username'
            name='username'
            autoComplete='username'
            value={formikCtx.values.username}
            onChange={formikCtx.handleChange}
          />
          <FormInput
            isRequired
            label={text.set_password}
            wasSubmitted={wasSubmitted}
            id='password'
            name='password'
            autoComplete='new-password'
            value={formikCtx.values.password}
            placeholder={text.enter_password}
            onChange={formikCtx.handleChange}
            error={formikCtx.errors.password}
            isPassword
          />
          <FormInput
            isRequired
            label={text.confirm_password}
            wasSubmitted={wasSubmitted}
            id='confirmPassword'
            name='confirmPassword'
            autoComplete='new-password'
            value={formikCtx.values.confirmPassword}
            placeholder={text.enter_password}
            onChange={formikCtx.handleChange}
            error={formikCtx.errors.confirmPassword}
            isPassword
          />
          {!isValid && <FormError>{errorMessage}</FormError>}
        </GridItem>
        <GridItem
          area='texts'
          display='flex'
          gap={{ base: 0.5, md: 2.5 }}
          flexDirection='column'
          borderLeft={{ base: 'none', md: 'section' }}
          justifyContent='center'
          pl={{ base: 0, md: 8 }}
        >
          {validationsMap.map((config) => (
            <ValidationText
              key={config.text}
              value={password}
              validation={config.validation}
              wasSubmitted={wasSubmitted}
              text={config.text}
            />
          ))}
        </GridItem>
        <GridItem area='submit' display='flex' justifySelf='center'>
          <FormSubmit isLoading={isLoading} loadingText={text.logging_in}>
            {text.set_password}
          </FormSubmit>
        </GridItem>
      </Grid>
    </Box>
  );
};
